// chat/actions.ts — the chat control executor for Oriora Mood (cloned from Oriora Expense's
// src/chat/actions.ts). The client router (chat/brain.ts) maps ONE user message → ONE ChatAction;
// this file EXECUTES that action against the on-device store (src/store.ts). Everything here is
// LOCAL SQLite over Tauri IPC — no network, no billing — EXCEPT the `reflect` action, which reuses
// the app's EXISTING live endpoint (reflectWeek → /api/apps/mood_tracker/reflect-week). No new
// server routes are added.
//
// HITL discipline (mirrors Expense): the destructive action (delete_entry) is NEVER executed from a
// chat turn on its own — the Chat UI holds it and requires an explicit Confirm tap first. This
// executor just performs the delete once the UI hands it back.

import {
  store, emojiForScore, labelForScore, normalizeTag, normalizeTags, todayLocal,
  computeWeeklyTrend, trendSummaryCopy, computeStreak, streakCopy,
  type MoodEntry, type MoodEntryPatch,
} from "../store";
import { save as saveFileDialog } from "@tauri-apps/plugin-dialog";
import { writeTextFile } from "@tauri-apps/plugin-fs";
import { buildExport } from "../export";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "mood-tracker";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";
import { reflectWeek, askMoods, BrainError } from "../brain";

// Honest framing on every AI answer about the user's moods — same line the UI cards carry.
const NOT_ADVICE = "Describes your logged moods — not medical or mental-health advice.";

// A compact mood row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  logged_on: string;
  score: number;
  note: string;
  tags: string[];
}

// A small, on-device filter over mood entries. The store has no filter helper (unlike Expense's
// applyFilter), so the mirror is defined here against MoodEntry's own fields.
export interface MoodFilter {
  from?: string;      // inclusive YYYY-MM-DD
  to?: string;        // inclusive YYYY-MM-DD
  minScore?: number;  // 1..5
  maxScore?: number;  // 1..5
  tag?: string;       // normalized tag substring
  text?: string;      // note substring (case-insensitive)
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_entry"; score: number; logged_on: string; logged_at?: string; note?: string | null; tags?: string[] }
  | { kind: "edit_entry"; id: string; patch: MoodEntryPatch; label?: string }
  | { kind: "delete_entry"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "filter"; filter: MoodFilter; label?: string }
  | { kind: "summarize" }
  | { kind: "reflect" }
  | { kind: "ask"; question: string } // billed Q&A over the on-device signals (the "Ask your moods" flagship)
  | { kind: "export_csv" }
  | { kind: "rename_tag"; from: string; to: string } // bulk, across every entry
  | { kind: "delete_tag"; tag: string } // bulk removal — UI confirms before executeAction
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS (the library) — fleet-wide capability. The customer's own saved
  // plans/notes/summaries, device-only. Identical shape in every Oriora app.
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms before executeAction
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // the log changed → the main list should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

const fmtDay = (iso: string) =>
  new Date(iso.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });

/** Pure, on-device filter over the log — the mirror of Expense's store.applyFilter for Mood. */
export function applyMoodFilter(all: MoodEntry[], f: MoodFilter): MoodEntry[] {
  return all.filter((e) => {
    if (f.from && e.logged_on < f.from) return false;
    if (f.to && e.logged_on > f.to) return false;
    if (typeof f.minScore === "number" && e.score < f.minScore) return false;
    if (typeof f.maxScore === "number" && e.score > f.maxScore) return false;
    if (f.tag && !e.tags.some((t) => t.includes(f.tag!))) return false;
    if (f.text && !(e.note ?? "").toLowerCase().includes(f.text.toLowerCase())) return false;
    return true;
  });
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_entry": {
        const logged_on = a.logged_on || todayLocal();
        const tags = a.tags && a.tags.length ? normalizeTags(a.tags) : [];
        // ADDS, never overwrites. This used to silently UPDATE an existing day, because the store
        // allowed only one entry per day — so "I felt awful this evening" replaced the morning's
        // "good" and the day's real shape was lost. Several entries a day are the point now;
        // CHANGING one is what edit_entry is for.
        const e = await store.createEntry({
          score: a.score, logged_on, logged_at: a.logged_at, note: a.note ?? "", tags,
        });
        const tagStr = e.tags.length ? ` · ${e.tags.join(", ")}` : "";
        const at = e.logged_at ? ` at ${e.logged_at}` : "";
        const also = (await store.listEntries({ from: logged_on, to: logged_on, limit: 10 })).length > 1
          ? " (your second entry that day)" : "";
        return { ok: true, message: `Logged ${emojiForScore(e.score)} ${labelForScore(e.score)} for ${fmtDay(logged_on)}${at}${tagStr}.${also}`, changed: true };
      }

      case "edit_entry":
        await store.updateEntry(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the entry"}.`, changed: true };

      case "delete_entry":
        await store.deleteEntry(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the entry"}.`, changed: true };

      case "filter": {
        const all = await store.listEntries({ limit: 50000 });
        const rows = applyMoodFilter(all, a.filter);
        const avg = rows.length ? rows.reduce((s, e) => s + e.score, 0) / rows.length : 0;
        const forLabel = a.label ? ` for "${a.label}"` : "";
        const rendered: FilterRow[] = rows.slice(0, 50).map((e) => ({
          id: e.id, logged_on: e.logged_on, score: e.score, note: e.note, tags: e.tags,
        }));
        return {
          ok: true,
          message:
            `${rows.length} match${rows.length !== 1 ? "es" : ""}${forLabel}` +
            (rows.length ? ` · avg ${avg.toFixed(1)}/5` : "") +
            (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rendered,
        };
      }

      case "summarize": {
        const all = await store.listEntries({ limit: 50000 });
        if (all.length === 0) return { ok: true, message: "No moods logged yet — try \"log good today\" to start." };
        const trend = computeWeeklyTrend(all.map((e) => ({ logged_on: e.logged_on, score: e.score })));
        const streak = computeStreak(all.map((e) => e.logged_on));
        const parts = [trendSummaryCopy(trend)];
        if (trend.weekAverage !== null) parts.push(`Average mood this week: ${trend.weekAverage.toFixed(1)}/5.`);
        if (streak > 0) parts.push(streakCopy(streak) + ".");
        return { ok: true, message: parts.join("\n") };
      }

      case "reflect": {
        // A billed AI step — reuses the EXISTING live endpoint (reflect-week). Signals are
        // computed on-device first; only the derived aggregates are sent (see brain.ts / signals.rs).
        try {
          const sig = await store.computeSignals(todayLocal());
          const r = await reflectWeek(sig);
          const reflection = r.reflection.trim();
          if (!reflection) return { ok: true, message: "Not enough recent moods to reflect on yet — log a few more days." };
          return { ok: true, message: `${reflection}\n\n${NOT_ADVICE}` };
        } catch (e) {
          return { ok: false, message: e instanceof Error ? e.message : "Reflection failed — please try again." };
        }
      }

      case "ask": {
        // The other billed AI step — the "Ask your moods" flagship (G16: every brain endpoint
        // reachable from chat). Signals computed on-device; only aggregates are sent.
        try {
          const sig = await store.computeSignals(todayLocal());
          const r = await askMoods(a.question, sig);
          const answer = r.answer.trim();
          if (!answer) return { ok: true, message: "Nothing in your log answers that yet — log a few more days." };
          return { ok: true, message: `${answer}\n\n${NOT_ADVICE}` };
        } catch (e) {
          return { ok: false, message: e instanceof Error ? e.message : "Couldn't answer — please try again." };
        }
      }

      case "export_csv": {
        const all = await store.listEntries({ limit: 50000 });
        if (all.length === 0) return { ok: true, message: "No moods logged yet — nothing to export." };
        const { csv, filename } = buildExport(all);
        const p = await saveFileDialog({ defaultPath: filename });
        if (!p) return { ok: true, message: "Cancelled — no file saved." };
        await writeTextFile(p, csv);
        return { ok: true, message: `Exported ${all.length} entr${all.length === 1 ? "y" : "ies"} to ${filename}.` };
      }

      case "rename_tag": {
        // Bulk organization power (L1): rename a tag EVERYWHERE it appears.
        const from = normalizeTag(a.from);
        const to = normalizeTag(a.to);
        if (!from || !to) return { ok: false, message: "Tell me both names — e.g. \"rename tag work to job\"." };
        const all = await store.listEntries({ limit: 50000 });
        const hits = all.filter((e) => e.tags.includes(from));
        if (hits.length === 0) return { ok: true, message: `No entries are tagged "${from}".` };
        for (const e of hits) {
          await store.updateEntry(e.id, { tags: normalizeTags(e.tags.map((t) => (t === from ? to : t))) });
        }
        return { ok: true, message: `Renamed tag "${from}" → "${to}" on ${hits.length} entr${hits.length === 1 ? "y" : "ies"}.`, changed: true };
      }

      case "delete_tag": {
        // Bulk removal — the Chat UI holds this for a Confirm tap before it reaches here.
        const tag = normalizeTag(a.tag);
        if (!tag) return { ok: false, message: "Which tag should I remove?" };
        const all = await store.listEntries({ limit: 50000 });
        const hits = all.filter((e) => e.tags.includes(tag));
        if (hits.length === 0) return { ok: true, message: `No entries are tagged "${tag}".` };
        for (const e of hits) {
          await store.updateEntry(e.id, { tags: e.tags.filter((t) => t !== tag) });
        }
        return { ok: true, message: `Removed tag "${tag}" from ${hits.length} entr${hits.length === 1 ? "y" : "ies"}.`, changed: true };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my moods" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole mood log). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library). Device-only; the message reports what ACTUALLY
      // happened, never the model's claim (APP-CHAT-AGENT-STANDARD §2.3).
      case "library_save": {
        const it = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${it.title}" to your library — on this device, yours.` };
      }
      case "library_list": {
        const items = libraryList(a.folder);
        if (!items.length) {
          return { ok: true, message: a.folder
            ? `Nothing in "${a.folder}" yet.`
            : "Your library is empty — ask me to write you something and I'll keep it here." };
        }
        const lines = items.map((i) => `• ${i.title}${i.folder ? ` — ${i.folder}` : ""}`).join("\n");
        return { ok: true, message: `${items.length} item${items.length === 1 ? "" : "s"} in your library:\n${lines}` };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "that item"} from your library.` }
          : { ok: false, message: "I couldn't find that item in your library." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} into "${a.folder}".` }
          : { ok: false, message: "I couldn't find that item in your library." };
      case "library_merge": {
        const merged = libraryMerge(a.ids, a.title);
        return merged
          ? { ok: true, message: `Merged ${a.label ?? "those items"} into "${merged.title}".` }
          : { ok: false, message: "I couldn't merge those — check the titles." };
      }

      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
